// src/components/HelpDialog.jsx
// Help (3.29.3), from the profile menu: a search box for the Library.
// 3.35.0: searches the Library articles this person can read (the server
// decides which), by title and text, and opens the one they pick.
// 3.36.2: better ranking (helpSearch).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { API_BASE } from '../firebase/config';
import Loader from './Loader';
import Emo from './Emo';

// Words that don't help find an article
const STOP = new Set('a an the to of in on for and or is are do does did how i my me can we you your it this that with from at by be what when where why which who get set use using out up'.split(' '));
// 3.36.2: rarer words count more; everyday words also find the portal's words
const SYN = { received: ['inbox', 'incoming'], receive: ['inbox', 'incoming'], incoming: ['inbox'], assistant: ['staff', 'people', 'person'], user: ['people', 'person', 'staff'], users: ['people', 'staff'], person: ['people', 'staff'], people: ['staff'], employee: ['people', 'person', 'staff'], block: ['blocked'], left: ['remove', 'removed', 'leaves'], leave: ['remove', 'removed'], quit: ['remove', 'removed'],
  colleague: ['people', 'person'], contract: ['documents', 'baa'], password: ['password'], cancel: ['cancel'], retry: ['retries', 'retry'], failed: ['fails', 'failed'], mailbox: ['inbox'], sent: ['outbox'] };
const stemOf = w => (w.length > 5 ? w.slice(0, w.length - 2) : w);
// Everyday verbs that say little about which article answers (never the key word, count half)
const WEAK = new Set('add new change make find see show open want need help know work works go put give take let tell look'.split(' '));
export function helpSearch(q, articles) {
  const raw = q.toLowerCase().replace(/[^a-z0-9\s-]/g, ' ').split(/\s+/).filter(w => w.length > 1 && !STOP.has(w));
  if (!raw.length) return [];
  const docs = articles.map(a => ({ a, title: (a.title || '').toLowerCase(), text: a.text.toLowerCase() }));
  const N = docs.length;
  const dfOf = stem => docs.filter(d => d.title.includes(stem) || d.text.includes(stem)).length;
  const terms = raw.map(w => ({ word: w, weak: WEAK.has(w), stems: [stemOf(w), ...(SYN[w] || []).map(stemOf)] }));
  for (const t of terms) { t.df = Math.max(...t.stems.map(dfOf)); t.w = (t.df ? Math.log(1 + N / t.df) : 0) * (t.weak ? 0.5 : 1); }
  const pairs = raw.slice(1).map((w, i) => `${raw[i]} ${w}`);
  // The question's most specific word must be in the article (else it doesn't answer it)
  // (and if no article has it at all, nothing here answers the question)
  const strong = terms.filter(t => !t.weak);
  const key = (strong.length ? strong : terms).reduce((m, t) => (t.df < m.df ? t : m), (strong.length ? strong : terms)[0]);
  if (!key.df) return [];
  const scored = docs.map(d => { let score = 0, hits = 0, hasKey = false;
    for (const t of terms) {
      let best = 0;
      // In the title: a feature name it stands for (inbox for "received") counts most; a weak verb never
      t.stems.forEach((st, i) => { let x = 0; if (!t.weak && d.title.includes(st)) x += i ? 6 : 3; const n = d.text.split(st).length - 1; if (n) x += 1 + Math.min(2, n - 1) * 0.5; best = Math.max(best, x); });
      if (best) { hits++; score += best * t.w; if (t === key) hasKey = true; }
    }
    // The question's words side by side in the article ("new client")
    for (const p of pairs) if (d.title.includes(p) || d.text.includes(p)) score += 2;
    return { a: d.a, score: hasKey ? score * (hits / terms.length) : 0 };   // articles that answer more of the question rank higher
  });
  return scored.filter(x => x.score > 0).sort((x, y) => y.score - x.score).slice(0, 6);
}
const textOf = html => (new DOMParser().parseFromString(String(html || '').replace(/<\/(p|div|li|h[1-6])>|<br\s*\/?>/gi, m => ` ${m}`), 'text/html').body.textContent || '').replace(/\s+/g, ' ').trim();

export default function HelpDialog({ onClose, onOpenLibrary }) {
  const { getToken } = useAuth();
  const [q, setQ] = useState('');
  const [articles, setArticles] = useState(null);   // null = loading
  const [failed, setFailed] = useState(false);
  const inputRef = useRef(null);
  const returnRef = useRef(null);
  // 3.57.5: once, when the window opens (the parent passes a new onClose on
  // every render; re-running moved the cursor to ✕ on every refresh, e.g. a
  // fax pop-up, and the next Space/Enter closed the window)
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    returnRef.current = document.activeElement;
    inputRef.current?.focus();
    function onKey(e) { if (e.key === 'Escape') { e.preventDefault(); onCloseRef.current(); } }
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); returnRef.current?.focus?.(); };
  }, []);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/portal/kb`, { headers: { Authorization: `Bearer ${await getToken()}` } });
        const j = await res.json();
        if (!res.ok) throw new Error(j.error);
        // Published only (Super Admins also get drafts and the archive)
        if (live) setArticles((j.data || []).filter(a => !a.status || a.status === 'published').map(a => ({ ...a, text: textOf(a.content) })));
      } catch { if (live) { setFailed(true); setArticles([]); } }
    })();
    return () => { live = false; };
  }, [getToken]);

  // The best articles for the question (helpSearch: rarer words count more,
  // everyday words also find the portal's names, nothing when no article has
  // the question's key word)
  const results = useMemo(() => (articles ? helpSearch(q, articles) : []), [q, articles]);

  const snippet = (a) => {
    const words = q.toLowerCase().split(/\s+/).filter(w => w.length > 2 && !STOP.has(w));
    const lower = a.text.toLowerCase();
    const at = words.map(w => lower.indexOf(w)).filter(i => i >= 0).sort((x, y) => x - y)[0] ?? 0;
    const start = at > 60 ? a.text.lastIndexOf(' ', at - 40) + 1 : 0;
    return (start ? '…' : '') + a.text.slice(start, start + 150) + (start + 150 < a.text.length ? '…' : '');
  };

  return (
    <div style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={S.modal} role="dialog" aria-modal="true" aria-labelledby="help-title">
        <div style={S.header} className="dlg-head">
          <div>
            <h2 id="help-title" style={S.title}><Emo e="❓" /> How do I…?</h2>
            <div style={S.sub}>Ask how to do something in the Fax Portal.</div>
          </div>
          <button onClick={onClose} style={S.closeBtn} aria-label="Close">✕</button>
        </div>
        <div style={S.body}>
          <form onSubmit={e => { e.preventDefault(); if (results[0]) onOpenLibrary(results[0].a.id); }} style={{ display: 'flex', gap: 8 }}>
            <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} placeholder="For example: how do I resend a failed fax?"
              aria-label="Search help" style={S.input} />
          </form>
          {articles === null ? <Loader size="sm" label="Loading the Library…" />
            : failed ? <div style={S.note}>Help couldn’t reach the Library. Try again, or open the <button type="button" onClick={() => onOpenLibrary()} style={S.link}>Library</button>.</div>
            : !q.trim() ? <div style={S.hint}>Type a few words: Help finds the Library articles that answer them.</div>
            : !results.length ? (
              <div style={S.note}>No article matches that yet. Try other words, or browse the <button type="button" onClick={() => onOpenLibrary()} style={S.link}>Library</button>.</div>
            ) : (
              <div style={{ marginTop: 12, display: 'grid', gap: 6 }} aria-live="polite">
                {results.map(({ a }) => (
                  <button key={a.id} type="button" onClick={() => onOpenLibrary(a.id)} style={S.result}>
                    <strong style={{ color: 'var(--t-00695c)', fontSize: 14 }}>{a.title}</strong>
                    <span style={{ fontSize: 12, color: 'var(--t-607d8b)', lineHeight: 1.45 }}>{snippet(a)}</span>
                  </button>
                ))}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgb(var(--c-0f2823-rgb) / 0.45)', zIndex: 1200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '12vh 16px 16px' },
  modal: { background: 'var(--c-ffffff)', borderRadius: 14, width: 'min(620px, 100%)', boxShadow: '0 20px 60px rgb(var(--c-000000-rgb) / 0.25)', animation: 'badgePop 0.2s ease-out' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, padding: '18px 22px', borderBottom: '2px solid var(--c-e0f2f1)', background: 'var(--c-f9fafb)', borderRadius: '14px 14px 0 0' },
  title: { margin: 0, fontSize: 19, fontWeight: 700, color: 'var(--t-00695c)', fontFamily: 'inherit' },
  sub: { fontSize: 13, color: 'var(--t-78909c)', marginTop: 2 },
  closeBtn: { width: 34, height: 34, borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', background: 'var(--c-ffffff)', cursor: 'pointer', fontSize: 15, color: 'var(--t-546e7a)', fontFamily: 'inherit', flexShrink: 0 },
  body: { padding: '18px 22px 22px' },
  input: { flex: 1, padding: '11px 14px', borderRadius: 10, border: '1.5px solid var(--c-b2dfdb)', fontSize: 15, fontFamily: 'inherit' },
  searchBtn: { padding: '0 18px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' },
  hint: { marginTop: 12, fontSize: 13, color: 'var(--t-90a4ae)' },
  note: { marginTop: 14, background: 'var(--c-fff8e1)', color: 'var(--t-6d4c00)', borderRadius: 10, padding: '12px 14px', fontSize: 13, lineHeight: 1.6 },
  result: { display: 'grid', gap: 3, textAlign: 'left', background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 10, padding: '10px 12px', cursor: 'pointer', fontFamily: 'inherit' },
  link: { background: 'none', border: 'none', padding: 0, color: 'var(--t-00695c)', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, textDecoration: 'underline' },
};
